import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Layout } from 'lucide-react';
import HistoryDialog from './HistoryDialog';
import UpdatedAtBadge from './UpdatedAtBadge';
import DraftBanner from './DraftBanner';
import { useAutoSaveDraft } from '../../hooks/useAutoSaveDraft';

const defaults = {
  cta_kicker: 'Make an impact today',
  cta_headline_prefix: 'Help us change another',
  cta_headline_emphasis: '15,000 lives',
  cta_button_label: 'Donate Now',
  address_lines: ['4B, Persiaran Zaaba,', 'Taman Tun Dr Ismail,', '60000 Kuala Lumpur'],
  phone: '03-7498 7140',
  phone_href: 'tel:+60374987140',
  whatsapp_display: '+60 12-675 9467 (WhatsApp)',
  whatsapp_href: 'https://wa.me/60126759467',
  email: 'admin@ewrf.org.my',
  facebook_url: '',
  instagram_url: '',
  twitter_url: '',
  youtube_url: '',
  linkedin_url: '',
  copyright_text: '© 2026 EWRF Malaysia · All rights reserved · Reg. PPM-001-14-02011979 · Tax-exempt under Section 44(6) 🇲🇾',
};

const FooterManager = () => {
  const [d, setD] = useState(defaults);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    try {
      const res = await adminApi.get('/footer');
      if (res.data) setD({ ...defaults, ...res.data });
    } catch (e) { /* ignore */ }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const { hasDraft, draftAt, readDraft, discardDraft, markSaved } = useAutoSaveDraft({
    storageKey: 'draft:footer',
    data: d,
    enabled: loaded,
    savedAt: d.updated_at,
  });

  const restoreDraft = () => {
    const draft = readDraft();
    if (draft) setD({ ...defaults, ...draft });
    discardDraft();
  };

  const set = (k, v) => setD((cur) => ({ ...cur, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.post('/footer', d);
      const res = await adminApi.get('/footer');
      if (res.data) setD({ ...defaults, ...res.data });
      markSaved(res.data || d);
      toast({ title: 'Footer saved', description: 'Changes are live.' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="footer-manager">
      {hasDraft && (
        <DraftBanner
          draftAt={draftAt}
          onRestore={restoreDraft}
          onDiscard={discardDraft}
          testid="footer-draft-banner"
        />
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layout className="w-6 h-6 text-[#1565C0]" />
          <div>
            <h2 className="text-2xl font-bold">Footer</h2>
            <p className="text-xs text-gray-500">Edit CTA, contact info, socials and copyright.</p>
          </div>
          <UpdatedAtBadge updatedAt={d.updated_at} />
        </div>
        <HistoryDialog collection="footer" docId="footer" onRestored={load} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Top CTA Strip</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          <div><Label>Kicker</Label><Input value={d.cta_kicker} onChange={(e)=>set('cta_kicker',e.target.value)} data-testid="footer-cta-kicker" /></div>
          <div><Label>Button Label</Label><Input value={d.cta_button_label} onChange={(e)=>set('cta_button_label',e.target.value)} data-testid="footer-cta-btn" /></div>
          <div><Label>Headline Prefix</Label><Input value={d.cta_headline_prefix} onChange={(e)=>set('cta_headline_prefix',e.target.value)} /></div>
          <div><Label>Headline Emphasis (gradient text)</Label><Input value={d.cta_headline_emphasis} onChange={(e)=>set('cta_headline_emphasis',e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Address (one line per row)</Label>
            <Textarea
              value={(d.address_lines || []).join('\n')}
              onChange={(e)=>set('address_lines', e.target.value.split('\n'))}
              rows={4}
              data-testid="footer-address"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Phone Display</Label><Input value={d.phone} onChange={(e)=>set('phone',e.target.value)} /></div>
            <div><Label>Phone Link (tel:)</Label><Input value={d.phone_href} onChange={(e)=>set('phone_href',e.target.value)} placeholder="tel:+60..." /></div>
            <div><Label>WhatsApp Display</Label><Input value={d.whatsapp_display} onChange={(e)=>set('whatsapp_display',e.target.value)} /></div>
            <div><Label>WhatsApp URL</Label><Input value={d.whatsapp_href} onChange={(e)=>set('whatsapp_href',e.target.value)} placeholder="https://wa.me/..." /></div>
            <div className="md:col-span-2"><Label>Email</Label><Input value={d.email} onChange={(e)=>set('email',e.target.value)} /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Social Media (leave blank to hide)</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-3">
          <div><Label>Facebook</Label><Input value={d.facebook_url} onChange={(e)=>set('facebook_url',e.target.value)} placeholder="https://facebook.com/..." /></div>
          <div><Label>Instagram</Label><Input value={d.instagram_url} onChange={(e)=>set('instagram_url',e.target.value)} placeholder="https://instagram.com/..." /></div>
          <div><Label>Twitter / X</Label><Input value={d.twitter_url} onChange={(e)=>set('twitter_url',e.target.value)} placeholder="https://x.com/..." /></div>
          <div><Label>YouTube</Label><Input value={d.youtube_url} onChange={(e)=>set('youtube_url',e.target.value)} placeholder="https://youtube.com/..." /></div>
          <div className="md:col-span-2"><Label>LinkedIn</Label><Input value={d.linkedin_url} onChange={(e)=>set('linkedin_url',e.target.value)} placeholder="https://linkedin.com/..." /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Copyright / Legal Line</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            value={d.copyright_text}
            onChange={(e)=>set('copyright_text',e.target.value)}
            rows={2}
            data-testid="footer-copyright"
          />
        </CardContent>
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#1565C0] shadow-2xl" data-testid="save-footer-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save Footer'}
        </Button>
      </div>
    </div>
  );
};

export default FooterManager;
